import { useQuery } from '@tanstack/react-query'
import type { FC } from 'react'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardFooter } from '@/components/card/card'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { Skeleton } from '@/components/skeleton/skeleton'

import { getApplicantDetailQuery } from '@/lib/queries/business/v1/applicant/queries'
import type { ResApplicantDetail } from '@/lib/queries/business/v1/types'
import { cn } from '@/lib/utils'

import { useContent } from './use-content'

export interface ContentProps {
  pathId: string
  onClose: () => void
  onEdited?: () => Promise<void>
}

export const Content: FC<ContentProps> = ({ pathId, onClose, onEdited }) => {
  const { data: dataApplicant, isFetching } = useQuery(getApplicantDetailQuery(pathId))

  if (isFetching || !dataApplicant) {
    return <Loading />
  }

  return <ContentWithApplicant pathId={pathId} onClose={onClose} onEdited={onEdited} applicant={dataApplicant} />
}

const ContentWithApplicant: FC<ContentProps & { applicant: ResApplicantDetail }> = ({
  pathId,
  onClose,
  onEdited,
  applicant,
}) => {
  const { form, formErrors, isPendingEditApplicant, handleSubmit } = useContent({
    pathId,
    applicant,
    onClose,
    onEdited,
  })

  return (
    <Card>
      <Form {...form}>
        <form>
          <CardContent className="p-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                rules={{ required: true }}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name*</FormLabel>
                    <FormControl>
                      <Input className="placeholder:text-faint-foreground" placeholder="Pietro Schirano" {...field} />
                    </FormControl>
                    <FormDescription>
                      You can add applicants with the same name. Please differentiate as needed.
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                rules={{ required: true }}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address*</FormLabel>
                    <FormControl>
                      <Input className="placeholder:text-faint-foreground" placeholder="email@example.com" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                rules={{ required: true }}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input className="placeholder:text-faint-foreground" placeholder="123 456 789" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        className="placeholder:text-faint-foreground"
                        placeholder="e.g.  Workers, Refugees, Asylum Seeker"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className={cn(!!formErrors.tags?.message && 'text-red-500')}>
                      {formErrors.tags?.message ?? 'Assign one or more tags to the applicant, separated by commas.'}
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>

          {/* SUBMIT */}
          <CardFooter>
            <Button disabled={isPendingEditApplicant} onClick={handleSubmit}>
              Save changes
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}

const Loading = () => {
  return (
    <Card className="h-[526px]">
      <CardContent className="space-y-4 p-5">
        <Skeleton className="h-5 w-2/6" />
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-5 w-3/6" />
        <Skeleton className="h-5 w-full" />
      </CardContent>
    </Card>
  )
}
